import { useId, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import GlobalStyles from "@mui/material/GlobalStyles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Markdown from "markdown-to-jsx";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";
import MDButton from "../MDButton";
import { getManualText, extractManualSection, slugify } from "../../Utils/manual";

function flattenText(node) {
  if (node === null || node === undefined) {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(flattenText).join("");
  }
  if (node.props && node.props.children) {
    return flattenText(node.props.children);
  }
  return "";
}

function makeHeadingComponent(Tag) {
  return function HeadingWithId({ children, ...rest }) {
    // markdown-to-jsx ya inyecta su propio "id" (sin tildes) en rest — va
    // después para que el nuestro (que sí preserva tildes, igual que los
    // links del índice) sea el que gane.
    const id = slugify(flattenText(children));
    return (
      <Tag {...rest} id={id} style={{ scrollMarginTop: 16 }}>
        {children}
      </Tag>
    );
  };
}

function AnchorConScroll({ href, children, ...rest }) {
  const handleClick = (event) => {
    if (href && href.startsWith("#")) {
      event.preventDefault();
      const target = document.getElementById(href.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };
  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}

// En MANUAL_USUARIO.md las imágenes usan rutas relativas a la raíz del repo
// (para que se vean en GitHub); sync-manual.js las copia a public/manual/.
function ImagenManual({ src, alt, ...rest }) {
  const esRelativa = src && !/^(https?:|data:|\/)/.test(src);
  const srcFinal = esRelativa ? `/manual/${src.split("/").pop()}` : src;
  return <img src={srcFinal} alt={alt} {...rest} />;
}

ImagenManual.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
};

ImagenManual.defaultProps = {
  src: "",
  alt: "",
};

const markdownOverrides = {
  img: { component: ImagenManual },
  h1: { component: makeHeadingComponent("h1") },
  h2: { component: makeHeadingComponent("h2") },
  h3: { component: makeHeadingComponent("h3") },
  h4: { component: makeHeadingComponent("h4") },
  h5: { component: makeHeadingComponent("h5") },
  h6: { component: makeHeadingComponent("h6") },
  a: { component: AnchorConScroll },
};

const markdownSx = {
  "& img": { maxWidth: "100%" },
  "& blockquote": {
    borderLeft: "4px solid #ccc",
    margin: 0,
    paddingLeft: "1rem",
    color: "text.secondary",
  },
  "& ul": {
    listStyle: "none",
    paddingLeft: "0.25rem",
    margin: "0.5rem 0",
  },
  "& ul li": {
    position: "relative",
    paddingLeft: "1.25rem",
    marginBottom: "0.35rem",
  },
  "& ul li::before": {
    content: '"→"',
    position: "absolute",
    left: 0,
    color: "text.secondary",
    fontSize: "0.85em",
  },
  "& ol": {
    paddingLeft: "1.25rem",
  },
  "& ol li": {
    marginBottom: "0.35rem",
  },
};

function ManualAyuda({ seccion, iconColor, soloCompleto }) {
  // useId() devuelve algo como ":r0:" — los ":" son inválidos en un
  // selector CSS sin escapar, así que se sacan para poder usarlo tal cual.
  const printAreaId = useId().replace(/:/g, "");
  const [open, setOpen] = useState(false);
  const [contenido, setContenido] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);

  const [mostrandoCompleto, setMostrandoCompleto] = useState(soloCompleto);
  const [manualCompleto, setManualCompleto] = useState(null);
  const [cargandoCompleto, setCargandoCompleto] = useState(false);

  const handleVerCompleto = () => {
    setMostrandoCompleto(true);
    if (manualCompleto || cargandoCompleto) {
      return;
    }
    setCargandoCompleto(true);
    getManualText()
      .then((texto) => setManualCompleto(texto))
      .catch(() => setError(true))
      .finally(() => setCargandoCompleto(false));
  };

  const handleOpen = () => {
    setOpen(true);
    setError(false);

    if (soloCompleto) {
      handleVerCompleto();
      return;
    }

    setMostrandoCompleto(false);
    if (contenido || cargando) {
      return;
    }
    setCargando(true);
    getManualText()
      .then((texto) => {
        const seccionTexto = extractManualSection(texto, seccion);
        setContenido(seccionTexto || "No se encontró esta sección en el manual.");
      })
      .catch(() => setError(true))
      .finally(() => setCargando(false));
  };

  const handleImprimir = () => window.print();

  return (
    <>
      <Tooltip title={soloCompleto ? "Manual de Usuario" : "Ayuda de esta pantalla"}>
        <IconButton onClick={handleOpen} sx={{ color: iconColor }}>
          {soloCompleto ? <MenuBookOutlinedIcon /> : <HelpOutlineIcon />}
        </IconButton>
      </Tooltip>
      <GlobalStyles
        styles={{
          "@media print": {
            body: { visibility: "hidden" },
            ".no-print": { display: "none !important" },
            [`#${printAreaId}`]: {
              visibility: "visible",
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              padding: "24px",
            },
            [`#${printAreaId} *`]: { visibility: "visible" },
          },
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <MDBox display="flex" justifyContent="space-between" alignItems="center" px={3} py={2}>
          <MDTypography variant="h6">
            {mostrandoCompleto ? "Manual de Usuario completo" : "Ayuda"}
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip title="Imprimir">
              <IconButton
                onClick={handleImprimir}
                size="small"
                sx={{
                  color: "text.secondary",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.1)" },
                }}
              >
                <PrintOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setOpen(false)}
              size="small"
              sx={{
                color: "text.secondary",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.1)" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </MDBox>
        <DialogContent dividers>
          <MDBox id={printAreaId}>
            {mostrandoCompleto ? (
              <>
                {!soloCompleto && (
                  <MDBox mb={2} className="no-print">
                    <MDButton
                      variant="text"
                      color="info"
                      size="small"
                      onClick={() => setMostrandoCompleto(false)}
                    >
                      <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Volver a esta sección
                    </MDButton>
                  </MDBox>
                )}
                {cargandoCompleto && <MDTypography variant="button">Cargando manual...</MDTypography>}
                {manualCompleto && (
                  <MDBox sx={markdownSx}>
                    <Markdown options={{ overrides: markdownOverrides }}>{manualCompleto}</Markdown>
                  </MDBox>
                )}
              </>
            ) : (
              <>
                {cargando && <MDTypography variant="button">Cargando ayuda...</MDTypography>}
                {error && (
                  <MDTypography variant="button" color="error">
                    No se pudo cargar el manual de usuario.
                  </MDTypography>
                )}
                {contenido && (
                  <>
                    <MDBox sx={markdownSx}>
                      <Markdown options={{ overrides: markdownOverrides }}>{contenido}</Markdown>
                    </MDBox>
                    <MDBox mt={3} display="flex" justifyContent="flex-end" className="no-print">
                      <MDButton variant="text" color="info" size="small" onClick={handleVerCompleto}>
                        Ver manual completo
                        <ArrowForwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </MDButton>
                    </MDBox>
                  </>
                )}
              </>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
}

ManualAyuda.defaultProps = {
  iconColor: "white",
  seccion: null,
  soloCompleto: false,
};

ManualAyuda.propTypes = {
  seccion: PropTypes.string,
  iconColor: PropTypes.string,
  soloCompleto: PropTypes.bool,
};

export default ManualAyuda;
